import { FC, useEffect, useState } from 'react';
import styles from './Result.module.scss';
import ClearSky from '../images/clearsky.svg';
import Moon from '../images/moon.svg';
import { StandardButton } from '@cxc/react-fds-components';
import StackBarChart from '../Charts/StackBarChart/StackBarChart';
import RightArrow from '../images/right-arrow.svg';
import pattern from 'patternomaly';
import environment from '../../environment';
import BlueRectangular from '../images/blueRectangular.svg';
import YellowRectangular from '../images/yellowRectangular.svg';
import GrayRectangular from '../images/grayRectangular.svg';
import { useAtom } from 'jotai';
import { aemContent } from '../../state/globalState';
import { htmlReactParserWrapper } from '../../utils/htmlReactParserWrapper';
import { reactToHTMLParseType } from '../../types/customTypes';
import { useUniversalContext } from '@cxc/react-universal-components';
import { AnalyticsConstants, LocalesConstants } from '../../utils/constants';
import { constructDigitalData, fireAnalyticsEvent } from '../../analytics/analytics';
import { PAGE_NAME, REFERRAL_EXIT, REFERRAL_EXIT_ACTION, REFERRAL_EXIT_TOOL, VEHICLE_DETAILS_BUTTON_ONCLICKS } from '../../analytics/analyticsConstants';

const Result: FC<any> = ({ data }) => {
    const [aem] = useAtom(aemContent);
    const { language, isLoggedIn, subscribeScopedValue } = useUniversalContext();
    const cookiePreferences = subscribeScopedValue(AnalyticsConstants.FPG_COOKIES, AnalyticsConstants.COOKIE_PREFERENCES);
    const seeVehicleDetailsLink = language == LocalesConstants.USLocale ? environment.FORDPRO_URL + '/en-us/fleet-vehicles/e-transit?intcmp=fpro-esa-ft-SeeVehicleDetails' : htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[3]?.['ctaUrl']);
    const [chartPayload, setChartPayload] = useState<any>(null);
    const [maxDaytimeTopUpCharge, setMaxDayTimeTopUpCharge] = useState<any | null>(null);
    const [decision, setDeciscion] = useState<any | null>(null);
    const [headingDecision, setHeadingDecision] = useState<any | null>(null);
    const [subHeadingDecision, setSubHeadingDecision] = useState<any | null>(null);
    const [description, setDescription] = useState<any | null>(null);
    const [colorIcon, setColorIcon] = useState<string | null>(null);
    const [reduceOpacity, setReduceOpacity] = useState<boolean>(false);
    const [showDayTimeLegend, setShowDayTimeLegend] = useState<boolean>(true);

    // aem content
    const suitableDecisionLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[0]?.['heading'] || null);
    const suitableDecisionHeading = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[2]?.['heading'] || null);
    const suitableDecisionSubHeading = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[2]?.['subHeading'] || null);
    const suitableMaxChargeLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[4]?.['textTwo'] || null);
    const suitableDescription = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[4]?.['subHeading'] || null);

    const vehicleTypeLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[11]?.['fieldReference']?.[2]?.['subHeading'] || null);
    const batteryValue = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[3]?.['fieldReference']?.[0]?.['heading'] || null);
    const batteryLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[3]?.['fieldReference']?.[0]?.['subHeading'] || null);
    const chargingTime = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[3]?.['fieldReference']?.[1]?.['heading'] || null);
    const chargingTimeLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[3]?.['fieldReference']?.[1]?.['subHeading'] || null);
    const vehicleDetailsButtonLabel: any = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[3]?.['ctaText'] || null);
    const vehicleDetailsButtonAriaLabel = aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[3]?.['ctaAriaLabel'] || undefined;
    const oneOvernightChargeLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[4]?.['text'] || null);
    const barGraphTitle = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[5]?.['text'] || null);
    const barGraphDescription = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[5]?.['textTwo'] || null);
    const barChartLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[6]?.['fieldReference']?.[0]?.['heading'] || null);
    const overnightLegendLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[6]?.['fieldReference']?.[13]?.['text'] || null);
    const daytimeLegendLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[6]?.['fieldReference']?.[14]?.['text'] || null);
    const transitVanImage = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[1] || null);

    const switchUptoDecisionLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[10]?.['fieldReference']?.[0]?.['heading'] || null);
    const switchUptoDecisionHeading = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[10]?.['fieldReference']?.[2]?.['heading'] || null);
    const switchUptoDecisionSubHeading = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[10]?.['fieldReference']?.[2]?.['subHeading'] || null);
    const switchUptoDescription = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[10]?.['fieldReference']?.[4]?.['subHeading'] || null);
    const switchUptoMaxChargeLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[10]?.['fieldReference']?.[4]?.['textTwo'] || null);

    const mayNotDecisionLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[11]?.['fieldReference']?.[0]?.['heading'] || null);
    const mayNotDecisionHeading = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[11]?.['fieldReference']?.[2]?.['heading'] || null);
    const mayNotDecisionSubHeading = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[11]?.['fieldReference']?.[2]?.['subHeading'] || null);
    const mayNotDescription = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[11]?.['fieldReference']?.[4]?.['subHeading'] || null);
    const mayNotMaxChargeLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[11]?.['fieldReference']?.[4]?.['textTwo'] || null);

    const months: reactToHTMLParseType[] = [];
    for (let i = 1; i <= 12; i++) {
        months.push(htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[6]?.['fieldReference']?.[i]?.['text'] || null));
    }

    useEffect(() => {
        if (data) {
            prepareDataForChart(data);
        }
    }, [data]);

    const prepareDataForChart = (data: any) => {
        const batteryEnergy = data.response.batteryEnergy;
        const charges = getChargesFromBatteryEnergy(batteryEnergy);
        const payload = {
            labels: months,
            datasets: [
                {
                    label: 'overnight charge',
                    data: charges.overnightCharges,
                    backgroundColor: '#00095B',
                },
                {
                    label: 'daytime pop-up charge',
                    data: charges.daytimePopUpCharges,
                    backgroundColor: [
                        pattern.draw('diagonal-right-left', 'rgb(23, 0, 244,0.8)'), // with opacity
                    ],
                },
            ],
        };
        setChartPayload(payload);
    };

    const getChargesFromBatteryEnergy = (batteryEnergy: any) => {
        const overnightCharges: number[] = [];
        const daytimePopUpCharges: number[] = [];
        for (const battery of batteryEnergy) {
            const comparisonToBatteryCapacity = battery.comparisonToBatteryCapacity[0]?.numberOfCharges;
            if (comparisonToBatteryCapacity > 1) {
                overnightCharges.push(1);
                daytimePopUpCharges.push(comparisonToBatteryCapacity - 1);
            } else {
                overnightCharges.push(comparisonToBatteryCapacity);
                daytimePopUpCharges.push(0);
            }
        }
        finalizeConclusion(daytimePopUpCharges);
        return { overnightCharges, daytimePopUpCharges };
    };

    const finalizeConclusion = (daytimePopUpCharges: any): void => {
        const calculateMaxDayTimeTopUpCharge: number = Math.max(...daytimePopUpCharges);
        setReduceOpacity(false);
        if (calculateMaxDayTimeTopUpCharge === 0) {
            setMaxDayTimeTopUpCharge(suitableMaxChargeLabel);
            setDeciscion(suitableDecisionLabel);
            setHeadingDecision(suitableDecisionHeading);
            setSubHeadingDecision(suitableDecisionSubHeading);
            setColorIcon(BlueRectangular);
            setDescription(suitableDescription);
            setShowDayTimeLegend(false);
        } else if (calculateMaxDayTimeTopUpCharge > 0 && calculateMaxDayTimeTopUpCharge <= 1) {
            setMaxDayTimeTopUpCharge(switchUptoMaxChargeLabel);
            setDeciscion(switchUptoDecisionLabel);
            setHeadingDecision(switchUptoDecisionHeading);
            setSubHeadingDecision(switchUptoDecisionSubHeading);
            setColorIcon(YellowRectangular);
            setDescription(switchUptoDescription);
        } else if (calculateMaxDayTimeTopUpCharge > 1) {
            setMaxDayTimeTopUpCharge(mayNotMaxChargeLabel);
            setDeciscion(mayNotDecisionLabel);
            setHeadingDecision(mayNotDecisionHeading);
            setSubHeadingDecision(mayNotDecisionSubHeading);
            setColorIcon(GrayRectangular);
            setReduceOpacity(true);
            setDescription(mayNotDescription);
        }
    };

    const getDecisionHeadingAndSubHeading = (headingDecision, subHeadingDecision) => {
        if (
            headingDecision &&
            subHeadingDecision &&
            headingDecision[0].includes(subHeadingDecision[0]) && //is E-Tranit present
            headingDecision?.[1]?.props?.children.includes(subHeadingDecision?.[1]?.props?.children) //is TM present as superscipt
        ) {
            return (
                <p className={styles.p}>
                    {headingDecision[0].split(subHeadingDecision[0])[0]}
                    <h4 className={styles.h4inline}>{subHeadingDecision}</h4>
                    {headingDecision[2]}
                </p>
            );
        } else {
            return (
                <span>
                    <p className={styles.p}>{headingDecision}</p>
                    <h4 className={styles.h4}>{subHeadingDecision}</h4>
                </span>
            );
        }
    };

    const handleSeeVehicleDetailsAnalyticsEvent = () => {
        if (language !== LocalesConstants.USLocale) {
            const digitalData = constructDigitalData(REFERRAL_EXIT_ACTION, PAGE_NAME, REFERRAL_EXIT, REFERRAL_EXIT_TOOL, '', VEHICLE_DETAILS_BUTTON_ONCLICKS, isLoggedIn, cookiePreferences);
            fireAnalyticsEvent(digitalData.digitalData, digitalData.directCallName);
        }
    };

    return (
        <div className={styles.content}>
            <div className={styles.headerWrapper}>
                {decision && (
                    <h4 className={styles.headerTitle}>
                        {' '}
                        {colorIcon && <img data-testid="rectangular-icon" style={{ marginRight: '12px', marginBottom: '5px' }} src={colorIcon} />}
                        {decision}
                    </h4>
                )}
            </div>
            <div className={styles.imageWrapper}>
                <img className={`${styles.transitImage} ${reduceOpacity === true ? styles.reduceOpacity : ''}`} src={environment.AEM_URL + transitVanImage} alt="transit" />
            </div>
            {getDecisionHeadingAndSubHeading(headingDecision, subHeadingDecision)}
            <div className={styles.line}></div>
            <div className={styles.infoWrapper}>
                <div className={styles.infoPair}>
                    <h4 className={styles.h4}>{batteryValue}</h4>
                    <p className={styles.p}>{batteryLabel}</p>
                </div>
                <div className={styles.infoPair}>
                    <h4 className={styles.h4}>{chargingTime}</h4>
                    <p className={styles.p}>{chargingTimeLabel}</p>
                </div>
                <div className={`${styles.infoPair} ${styles.hideMobile}`}>
                    <div className={`${styles.buttonWrapper} ${styles.fullWidth}`}>
                        <StandardButton
                            data-testid="see-vehicle-details-btn"
                            ariaLabel={vehicleDetailsButtonAriaLabel}
                            icon={<img src={RightArrow} />}
                            iconAlignment="right"
                            outlined
                            type="submit"
                            as="a"
                            title={vehicleDetailsButtonLabel}
                            href={seeVehicleDetailsLink}
                            target="_blank"
                            onClick={handleSeeVehicleDetailsAnalyticsEvent}
                        />
                    </div>
                </div>
            </div>
            <div className={`${styles.infoWrapper} ${styles.hideDesktop}`}>
                <div className={`${styles.buttonWrapper} ${styles.fullWidth}`}>
                    <StandardButton
                        data-testid="see-vehicle-details-btn"
                        ariaLabel={vehicleDetailsButtonAriaLabel}
                        icon={<img src={RightArrow} />}
                        iconAlignment="right"
                        outlined
                        type="submit"
                        as="a"
                        title={vehicleDetailsButtonLabel}
                        href={seeVehicleDetailsLink}
                        target="_blank"
                        onClick={handleSeeVehicleDetailsAnalyticsEvent}
                    />
                </div>
            </div>
            <div className={styles.line}></div>
            <div className={styles.grayBox}>
                <p className={styles.p} data-testid="description">
                    {description}
                </p>
                <div className={styles.infoWrapper}>
                    <img className={styles.iconImage} src={Moon} alt="moon" /> <p className={styles.iconDetail}>{oneOvernightChargeLabel}</p>
                </div>
                {maxDaytimeTopUpCharge && (
                    <div className={styles.infoWrapper}>
                        <img className={styles.iconImage} src={ClearSky} alt="clearsky" /> <p className={styles.iconDetail}>{maxDaytimeTopUpCharge}</p>
                    </div>
                )}
            </div>
            <p className={styles.p}>{barGraphTitle}</p>
            <br />
            <p className={styles.p}>{barGraphDescription}</p>
            {chartPayload && <StackBarChart chartPayload={chartPayload} showDayTimeLegend={showDayTimeLegend} chartTitle={barChartLabel} overnightLegendLabel={overnightLegendLabel} daytimeLegendLabel={daytimeLegendLabel} />}
        </div>
    );
};

export default Result;
